import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { erApiFeil } from '../api/feil.ts';
import { useKjenteFeil, useKjentFeil, useOppdaterKjentFeil, useOpprettKjentFeil, type KnownIssue, type KnownIssueUpdate } from '../api/useKnownIssues.ts';
import { useMe } from '../api/useMe.ts';
import { INNSPILL } from '../skall/rom.ts';
import { tilgangFra } from '../skall/tilgang.ts';
import { useProdukter } from '../api/useProducts.ts';
import { useSak } from '../api/useSubmissions.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { saksnr } from '../innboks/saksnummer.ts';
import { Toast, type ToastInnhold } from '../innboks/Toast.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { Merke } from '../skall/Merke.tsx';
import { KjentFeilDetalj, LESER_GRUNN_KF } from './Detalj.tsx';
import { KjentFeilRad } from './Rad.tsx';
import { RettetDialog } from './RettetDialog.tsx';
import { Skjema, type SkjemaModus } from './Skjema.tsx';
import { Kartknapp } from '../innboks/Kartknapp.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import type { Tast } from '../innboks/Tastaturkart.tsx';

type Panelmodus = { navn: 'ingen' } | { navn: 'ny' } | { navn: 'rediger'; id: string };
type Filter = 'alle' | 'aktive' | 'rettet';

/**
 * Kjente feil-registeret (R3.3 — kjente feil-konsept v0.1, spesifikasjonen
 * `design/wireframes/admin-kjente-feil.jsx`): en LISTE med DETALJRUTE, som innboksen.
 * Raden bærer tittelen, tallet som har spurt og én meta-linje; detaljruten bærer helten,
 * DD-30s tre deler, synligheten som to brytere (DD-60: «I widgeten» og «I programmet» er
 * to FLATER, aldri et publikum) og den ene handlingen. Rettet varsler alle med Reach én
 * gang, med tallet og teksten vist FØR bekreftelse (DD-31). Registeret er det widgeten
 * søker i — tittelen er brukerens ord, aldri teknikk.
 */
/** Kjente feil-sidens egne taster — uendret fra M2.4 (avvik 6: ingen ny `e`). */
const TASTER: Tast[] = [
  ['n', 'ny kjent feil'],
  ['j / k', 'neste / forrige'],
  ['Enter', 'rediger valgt'],
  ['x', 'marker valgt som rettet'],
  ['/', 'søk'],
  ['Esc', 'lukk skjemaet — ut av feltet'],
];

export function KjenteFeilSide() {
  const me = useMe();
  // Rom-rollen i rommet siden hører til — aldri `role`, som bare sier eier eller medlem (M4.8, `skall/tilgang.ts`).
  const kanSkrive = tilgangFra(me.data).kanSkrive(INNSPILL);
  const [params, setParams] = useSearchParams();
  const fraSakId = params.get('fraSak');
  const fraSak = useSak(fraSakId);
  const produkter = useProdukter();
  const liste = useKjenteFeil({});
  const feil = useMemo(() => liste.data?.pages.flatMap((p) => p.items) ?? [], [liste.data]);
  const opprett = useOpprettKjentFeil();
  const oppdater = useOppdaterKjentFeil();
  const opptatt = opprett.isPending || oppdater.isPending;

  const [sok, setSok] = useState('');
  // Søket er et ikon til det brukes (konseptets skjerm 1); `/` åpner feltet og tar markøren.
  const [sokAapen, setSokAapen] = useState(false);
  const sokRef = useRef<HTMLInputElement | null>(null);
  const [filter, setFilter] = useState<Filter>('alle');
  const [valgtId, setValgtId] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panelmodus>({ navn: 'ingen' });
  const [rettetFor, setRettetFor] = useState<string | null>(null);
  const smal = useSmal();
  const rettet = useKjentFeil(rettetFor);
  const [toast, setToast] = useState<ToastInnhold | null>(null);

  const synlige = useMemo(() => {
    const q = sok.trim().toLowerCase();
    const etterFilter = feil.filter((f) => (filter === 'alle' ? true : filter === 'aktive' ? f.status === 'aktiv' : f.status === 'fikset'));
    if (!q) return etterFilter;
    return etterFilter.filter((f) => f.title.toLowerCase().includes(q) || (f.workaround ?? '').toLowerCase().includes(q) || f.screens.some((s) => s.toLowerCase().includes(q)));
  }, [feil, sok, filter]);
  const aktive = feil.filter((f) => f.status === 'aktiv').length;

  useEffect(() => {
    if (sokAapen) sokRef.current?.focus();
  }, [sokAapen]);

  // Første oppføring velges når lista kommer. Under breakpointen (DD-51) er lista det
  // brukeren ser først: ingenting autovelges, og en rad som forsvinner sender tilbake dit.
  useEffect(() => {
    if (synlige.length === 0) {
      if (valgtId !== null) setValgtId(null);
      return;
    }
    if (valgtId === null) {
      if (!smal) setValgtId(synlige[0]!.id);
      return;
    }
    if (!synlige.some((f) => f.id === valgtId)) setValgtId(smal ? null : synlige[0]!.id);
  }, [synlige, valgtId, smal]);

  const valgt = feil.find((f) => f.id === valgtId) ?? null;
  const produktNavn = produkter.data?.find((p) => p.id === valgt?.productId)?.name;

  const visFeil = useCallback((e: unknown) => {
    setToast({ tekst: erApiFeil(e) ? `Kunne ikke lagre: ${e.brukertekst}` : 'Kunne ikke lagre. Prøv igjen.' });
  }, []);

  const skjemaModus = useMemo<SkjemaModus | null>(() => {
    if (fraSakId && fraSak.data) return { navn: 'fraSak', sak: fraSak.data };
    if (panel.navn === 'ny') return { navn: 'ny' };
    if (panel.navn === 'rediger') {
      const f = feil.find((x) => x.id === panel.id);
      return f ? { navn: 'rediger', feil: f } : null;
    }
    return null;
  }, [fraSakId, fraSak.data, panel, feil]);

  const lukkSkjema = useCallback(() => {
    setPanel({ navn: 'ingen' });
    if (fraSakId) setParams({});
  }, [fraSakId, setParams]);

  const bryter = useCallback(
    (f: KnownIssue, felt: 'visibleInWidget' | 'visibleToTesters', verdi: boolean) => {
      // Ordlisten (DD-60, P3): bryterens navn er flaten kortet vises PÅ, aldri et publikum.
      const navn = felt === 'visibleInWidget' ? 'I widgeten' : 'I programmet';
      oppdater
        .mutateAsync({ id: f.id, body: { [felt]: verdi } as KnownIssueUpdate })
        .then(() =>
          setToast({
            tekst: `«${navn}» ${verdi ? 'på' : 'av'} for «${f.title}».`,
            angre: () => {
              setToast(null);
              oppdater.mutateAsync({ id: f.id, body: { [felt]: !verdi } as KnownIssueUpdate }).catch(visFeil);
            },
          }),
        )
        .catch(visFeil);
    },
    [oppdater, visFeil],
  );

  const bekreftRettet = useCallback(
    (fixedIn: string) => {
      if (!rettet.data) return;
      const f = rettet.data;
      oppdater
        .mutateAsync({ id: f.id, body: { status: 'fikset', fixedIn } })
        .then((res) => {
          setRettetFor(null);
          setToast({ tekst: `«${f.title}» er rettet i ${fixedIn}. ${res.reach} ${res.reach === 1 ? 'innsender varsles' : 'innsendere varsles'} gjennom systemet deres — én gang.` });
        })
        .catch(visFeil);
    },
    [rettet.data, oppdater, visFeil],
  );

  // Tastaturet: j/k i lista, n ny, Enter rediger, x rettet, / søk, Esc lukk. Ikke i felter.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mål = e.target as HTMLElement | null;
      const iFelt = mål !== null && /^(INPUT|TEXTAREA|SELECT)$/.test(mål.tagName);
      if (e.key === 'Escape') {
        if (rettetFor) setRettetFor(null);
        else if (skjemaModus) lukkSkjema();
        else if (iFelt) mål.blur();
        return;
      }
      if (iFelt || rettetFor || e.ctrlKey || e.metaKey || e.altKey) return;
      switch (e.key) {
        case 'j':
        case 'k': {
          if (synlige.length === 0) return;
          const i = synlige.findIndex((f) => f.id === valgtId);
          const neste = synlige[Math.min(synlige.length - 1, Math.max(0, i + (e.key === 'j' ? 1 : -1)))];
          if (neste) setValgtId(neste.id);
          break;
        }
        // preventDefault: feltet som får fokus skal ikke få bokstaven som åpnet det.
        case 'n':
          if (kanSkrive) {
            e.preventDefault();
            setPanel({ navn: 'ny' });
          }
          break;
        case 'Enter':
          if (valgtId) {
            e.preventDefault();
            setPanel({ navn: 'rediger', id: valgtId });
          }
          break;
        case 'x':
          if (kanSkrive && valgt?.status === 'aktiv') {
            e.preventDefault();
            setRettetFor(valgt.id);
          }
          break;
        case '/':
          e.preventDefault();
          setSokAapen(true);
          sokRef.current?.focus();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [synlige, valgtId, valgt, kanSkrive, rettetFor, skjemaModus, lukkSkjema]);

  const leserGrunn = kanSkrive ? null : LESER_GRUNN_KF;
  // Tomruten tar hele flaten (DD-19) — men aldri mens et skjema er åpent: «Ny kjent feil» og
  // «Gjør til kjent feil» fra en sak er nettopp veien ut av et tomt register.
  const tomtRegister = !liste.isPending && !liste.isError && feil.length === 0 && !skjemaModus && !fraSakId;

  return (
    <>
      {/* Toppen bærer tittelen og tastaturkartet; tellerne, søket og «Ny kjent feil» står i
          listas eget hode, der de hører til (konseptets skjerm 1). */}
      <header className="panel-topp">
        <h1 className="panel-h1">Kjente feil</h1>
        <Kartknapp tittel="Kjente feil" taster={TASTER} />
      </header>

      {tomtRegister ? (
        <div className="panel-tomflate">
          {/* DD-19: én setning, én fylt knapp, og snarveien saken kommer fra. */}
          <div className="panel-tomrute" data-testid="kf-tomrute">
            <Merke storrelse="xl" dempet alene />
            <p className="panel-tomrute-tekst panel-tomrute-alle">Ingen kjente feil.</p>
            <p className="panel-tomrute-tekst">
              Første kommer fra en sak: åpne saken i <Link to="/innboks">Innboksen</Link> og velg <strong>Gjør til kjent feil</strong> <kbd>f</kbd>.
            </p>
            <Knapp label="Ny kjent feil" kbd="n" primaer grunn={leserGrunn} onClick={() => setPanel({ navn: 'ny' })} testid="kf-ny" />
          </div>
        </div>
      ) : (
        /* DD-51: under grensen er todelt-visningen én kolonne — lista, så ruten. */
        <div className="panel-todelt panel-kf-todelt" data-visning={smal ? (skjemaModus || valgtId !== null ? 'detalj' : 'liste') : undefined}>
          <section className="panel-liste panel-kf-liste" aria-label="Kjente feil">
            <div className="panel-kf-listehode">
              <div className="panel-kf-listetopp">
                <button type="button" className="panel-kf-ikonknapp" aria-label="Søk i kjente feil" aria-keyshortcuts="/" aria-pressed={sokAapen} onClick={() => setSokAapen((v) => !v)} data-testid="kf-sok-knapp">
                  <Ikon navn="search-outline" />
                </button>
                <Knapp label="Ny kjent feil" kbd="n" lenke grunn={leserGrunn} onClick={() => setPanel({ navn: 'ny' })} testid="kf-ny" />
              </div>
              {sokAapen ? (
                <label className="panel-sok">
                  <Ikon navn="search-outline" />
                  <input ref={sokRef} type="search" value={sok} onChange={(e) => setSok(e.target.value)} placeholder="Søk" aria-label="Søk i kjente feil" data-testid="kf-sok" />
                  <kbd aria-hidden="true">/</kbd>
                </label>
              ) : null}
              {/* Tellerne ER filtrene (samme mønster som innboksen og programsiden). */}
              {liste.data ? (
                <div className="panel-tellere" role="group" aria-label="Telling">
                  <button type="button" className="panel-teller" aria-pressed={filter === 'alle'} onClick={() => setFilter('alle')} data-testid="kf-filter-alle">
                    <b>{feil.length}</b> alle
                  </button>
                  <button type="button" className="panel-teller" aria-pressed={filter === 'aktive'} onClick={() => setFilter('aktive')} data-testid="kf-filter-aktive">
                    <b>{aktive}</b> aktive
                  </button>
                  <button type="button" className="panel-teller" aria-pressed={filter === 'rettet'} onClick={() => setFilter('rettet')} data-testid="kf-filter-rettet">
                    <b>{feil.length - aktive}</b> rettet
                  </button>
                </div>
              ) : null}
            </div>

            {liste.isPending ? (
              <div role="status" aria-busy="true" aria-label="Laster kjente feil" className="panel-skjelett">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="panel-skjelett-rad">
                    <span className="panel-skjelett-linje" />
                    <span className="panel-skjelett-linje panel-skjelett-kort" />
                  </div>
                ))}
              </div>
            ) : liste.isError ? (
              <div role="alert" className="panel-melding panel-melding-feil">
                <Ikon navn="warning-outline" /> Kunne ikke laste registeret. {erApiFeil(liste.error) ? liste.error.brukertekst : 'Sjekk nettet og prøv igjen.'}
                <button type="button" className="panel-knapp-liten" onClick={() => void liste.refetch()}>
                  Prøv igjen
                </button>
              </div>
            ) : synlige.length === 0 ? (
              <div className="panel-tom" role="status">
                <p className="panel-tom-tittel">{sok.trim() ? `Ingen treff på «${sok}».` : 'Ingen kjente feil med dette filteret.'}</p>
                {sok.trim() ? null : (
                  <p className="panel-tekst">
                    <button type="button" className="panel-knapp-liten" onClick={() => setFilter('alle')}>
                      Vis alle {feil.length}
                    </button>
                  </p>
                )}
              </div>
            ) : (
              <ul className="panel-rader">
                {synlige.map((f) => (
                  <KjentFeilRad key={f.id} f={f} valgt={f.id === valgtId} onVelg={() => setValgtId(f.id)} />
                ))}
              </ul>
            )}
            {liste.hasNextPage ? (
              <div className="panel-last-mer">
                <button type="button" className="panel-knapp-sekundaer" onClick={() => void liste.fetchNextPage()} disabled={liste.isFetchingNextPage}>
                  {liste.isFetchingNextPage ? 'Laster …' : 'Last flere'}
                </button>
              </div>
            ) : null}
          </section>

          <div className="panel-detalj-ramme">
            {smal && !skjemaModus && valgtId !== null ? (
              <button type="button" className="panel-knapp-sekundaer panel-tilbake" onClick={() => setValgtId(null)}>
                <Ikon navn="arrow-back-outline" /> Tilbake til lista
              </button>
            ) : null}
            {skjemaModus ? (
              <Skjema
                key={skjemaModus.navn === 'rediger' ? skjemaModus.feil.id : skjemaModus.navn === 'fraSak' ? skjemaModus.sak.id : 'ny'}
                modus={skjemaModus}
                produkter={produkter.data ?? []}
                produkterTilstand={produkter.isPending ? 'laster' : produkter.isError ? 'feil' : 'ok'}
                kanSkrive={kanSkrive}
                opptatt={opptatt}
                {...(smal ? { onTilbake: lukkSkjema } : {})}
                onOpprett={(body) => {
                  opprett
                    .mutateAsync(body)
                    .then((ny) => {
                      setValgtId(ny.id);
                      lukkSkjema();
                      setToast({
                        tekst: skjemaModus.navn === 'fraSak'
                          ? `Kjent feil opprettet fra sak ${saksnr(skjemaModus.sak.number)}. Saken står nå som «Kjent feil» — innsenderen har fått «Vi vet».`
                          : `«${ny.title}» er lagt i registeret${ny.visibleInWidget ? ' og vises i widgeten' : ''}.`,
                      });
                    })
                    .catch(visFeil);
                }}
                onOppdater={(id, body) => {
                  oppdater
                    .mutateAsync({ id, body })
                    .then((f) => {
                      lukkSkjema();
                      setToast({ tekst: `«${f.title}» er oppdatert.` });
                    })
                    .catch(visFeil);
                }}
                onAvbryt={lukkSkjema}
              />
            ) : fraSakId && fraSak.isPending ? (
              <div className="panel-skjelett" role="status" aria-busy="true" aria-label="Laster saken">
                <span className="panel-skjelett-linje panel-skjelett-tittel" />
              </div>
            ) : fraSakId && fraSak.isError ? (
              <div role="alert" className="panel-melding panel-melding-feil">
                <Ikon navn="warning-outline" /> Fant ikke saken. {erApiFeil(fraSak.error) ? fraSak.error.brukertekst : ''}
                <Knapp label="Lukk" kbd="Esc" onClick={lukkSkjema} />
              </div>
            ) : valgt ? (
              <KjentFeilDetalj
                f={valgt}
                {...(produktNavn ? { produktNavn } : {})}
                kanSkrive={kanSkrive}
                onBryter={(felt, verdi) => bryter(valgt, felt, verdi)}
                onRettet={() => setRettetFor(valgt.id)}
                onRediger={() => setPanel({ navn: 'rediger', id: valgt.id })}
              />
            ) : null}
          </div>
        </div>
      )}

      {rettetFor && rettet.data ? <RettetDialog feil={rettet.data} opptatt={oppdater.isPending} onBekreft={bekreftRettet} onAvbryt={() => setRettetFor(null)} /> : null}
      {toast ? <Toast innhold={toast} onLukk={() => setToast(null)} /> : null}
    </>
  );
}
